import type { ShapeBounds } from '@/lib/shapes/types/shape-bounds'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { snapLayoutBoxToCanvasZoomGrid } from '@/lib/transforms/canvas-layout-pixel-snap'
import { isGroupShape } from '@/lib/groups/group-kind'
import { LockedSelectionBadge } from '@/components/app/editor/selection-chrome/locked-selection-badge'

interface SelectionOutlineHighlightsProps {
  selectedShapes: ShapeData[]
  shapeBounds: Record<string, ShapeBounds>
  scale: number
  selectionOutlineDocWidth: number
  multiSelectionStrokeColor: string
  isLockedMultiSelected: boolean
  isFrozenOnlyMultiSelected: boolean
  onUnlockSelectionBadgeClick?: () => void
  onUnfreezeSelectionBadgeClick?: () => void
}

/**
 * Individual shape highlight outlines for a unified (mixed shape + text) selection, drawn inside
 * the zoom-scaled layer. See the comment where `ShapeManager` renders this for when it applies.
 */
export function SelectionOutlineHighlights({
  selectedShapes,
  shapeBounds,
  scale,
  selectionOutlineDocWidth,
  multiSelectionStrokeColor,
  isLockedMultiSelected,
  isFrozenOnlyMultiSelected,
  onUnlockSelectionBadgeClick,
  onUnfreezeSelectionBadgeClick,
}: SelectionOutlineHighlightsProps) {
  return (
    <>
      {selectedShapes.map((shape) => {
        const b = shapeBounds[shape.id]
        const fb = b ?? {
          x: 0,
          y: 0,
          width: shape.width ?? 0,
          height: shape.height ?? 0,
        }
        const pw = shape.width ?? 0
        const ph = shape.height ?? 0
        // Match `ShapeRenderer`: rotation is around the center of the placement rect (w×h), not
        // the center of the path bbox. Default origin would misalign triangles and other inset paths.
        const rotationOriginX = pw > 0 ? pw / 2 - fb.x : fb.width / 2
        const rotationOriginY = ph > 0 ? ph / 2 - fb.y : fb.height / 2

        const highlightSnap = snapLayoutBoxToCanvasZoomGrid(
          {
            x: shape.x + fb.x,
            y: shape.y + fb.y,
            width: fb.width,
            height: fb.height,
          },
          scale
        )

        return (
          <div
            key={`highlight-${shape.id}`}
            className="absolute pointer-events-none"
            style={{
              left: highlightSnap.x,
              top: highlightSnap.y,
              width: highlightSnap.width,
              height: highlightSnap.height,
              ...(isGroupShape(shape)
                ? {
                    border: 'none',
                    outline: `${selectionOutlineDocWidth}px solid ${multiSelectionStrokeColor}`,
                    outlineOffset: 0,
                  }
                : {
                    border: `${selectionOutlineDocWidth}px solid ${multiSelectionStrokeColor}`,
                  }),
              transform: shape.rotation ? `rotate(${shape.rotation}deg)` : undefined,
              transformOrigin: `${rotationOriginX}px ${rotationOriginY}px`,
              opacity: 0.7,
              zIndex: 49,
            }}
          >
            {isLockedMultiSelected && (
              <LockedSelectionBadge variant="lock" onUnlockClick={onUnlockSelectionBadgeClick} />
            )}
            {isFrozenOnlyMultiSelected && (
              <LockedSelectionBadge
                variant="freeze"
                onUnfreezeClick={onUnfreezeSelectionBadgeClick}
              />
            )}
          </div>
        )
      })}
    </>
  )
}
